import acequia from '../acequia.js'
import { config } from '../config.js'

let _authURL = config?.acequia?.authURL || '/auth'

let authReadyResolve, authReadyReject

let _authDeviceInfoCache = {} // cache deviceInfo

const authReady = new Promise((resolve, reject) => {
    authReadyResolve = resolve
    authReadyReject = reject
})

export { authReady }

export async function checkAuth() {
    let r = await acequia.acequiaReady()
    authReadyResolve(r)
    return authReady
}

export async function register(authURL) {
    authURL = authURL || _authURL

    let { deviceId, instanceId } = await checkAuth()

    let publicKey = await acequia.tokens.jose.exportJWK(
        acequia.getKeys().publicKey
    )

    // Include deviceName from IndexedDB if available
    const deviceName = await acequia.acequiaStore?.getItem?.('deviceName') || undefined

    let registerBody = {
        deviceId,
        instanceId,
        publicKey,
        deviceName,
    }

    let resp = await fetch(authURL + '/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerBody),
    })

    return resp
}

/**
 * returns deviceInfo for given authURL and id
 * deviceInfo is cached by serviceworker so subsequent requests will recieve same information
 * JBT need invalidation logic
 *
 * @param {*} id
 * @param {*} authURL
 * @returns
 */
export async function getDeviceInfo(id, authURL) {
    if (!authURL) authURL = _authURL

    let deviceInfo = _authDeviceInfoCache?.[authURL]?.[id]
    if (deviceInfo) return deviceInfo

    try {
        deviceInfo = await (await fetch(`${authURL}/deviceInfo/${id}`)).json()
        if (!_authDeviceInfoCache[authURL]) _authDeviceInfoCache[authURL] = {}
        _authDeviceInfoCache[authURL][id] = deviceInfo
        return deviceInfo
    } catch (e) {
        console.error(e)
        throw e
    }
}

export async function getPublicKey(id, authURL) {
    return (await getDeviceInfo(id, authURL)).publicKey
}

// ============================================
// Per-Device User Keys - User Management APIs
// ============================================

/**
 * Register a new user with their first device's public key.
 * If this is the first user for the domain, they become the owner.
 *
 * @param {string} authURL - Auth server URL
 * @param {Object} options - Optional profile fields
 * @param {string} options.handle - Required username handle
 * @param {string} options.firstName - Optional first name
 * @param {string} options.lastName - Optional last name
 * @param {string} options.displayName - Optional display name
 * @param {string} options.email - Optional email
 * @param {string} options.phone - Optional phone number
 * @returns {Promise<Object>} Registration response
 */
export async function registerUser(authURL, options = {}) {
    authURL = authURL || _authURL

    // Use userKeys (not device keys) - this matches what createUserToken uses
    let { userId, deviceId, userKeys } = await checkAuth()

    let publicKey = await acequia.tokens.jose.exportJWK(userKeys.publicKey)
    let kid = await acequia.tokens.jose.calculateJwkThumbprint(publicKey)

    // Include deviceName from IndexedDB if available
    const deviceName = await acequia.acequiaStore?.getItem?.('deviceName') || undefined

    let registerBody = {
        userId,
        deviceId,
        deviceName,
        publicKey,
        kid,
        handle: options.handle,
        firstName: options.firstName,
        lastName: options.lastName,
        displayName: options.displayName,
        email: options.email,
        phone: options.phone,
        registrationMessage: options.registrationMessage,
    }

    let resp = await fetch(authURL + '/users/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerBody),
    })

    const result = await resp.json()

    // Persist handle in IndexedDB so acequia.getHandle() works on subsequent loads
    if (result.success && options.handle && acequia.acequiaStore?.setItem) {
        await acequia.acequiaStore.setItem('handle', options.handle)
    }

    return result
}

/**
 * Add a new device key for the current user (endorsement flow).
 * Requires authentication with existing user token.
 *
 * @param {string} authURL - Auth server URL
 * @param {string} newDeviceId - New device's ID
 * @param {Object} newPublicKey - New device's public key (JWK)
 * @param {string} newKid - Key identifier for new key
 * @param {string} authToken - Auth token from existing device
 * @returns {Promise<Object>} Response
 */
export async function addUserKey(
    authURL,
    newDeviceId,
    newPublicKey,
    newKid,
    authToken
) {
    authURL = authURL || _authURL

    let { userId } = await checkAuth()

    let resp = await fetch(`${authURL}/users/${userId}/keys`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
            deviceId: newDeviceId,
            publicKey: newPublicKey,
            kid: newKid,
        }),
    })

    return resp.json()
}

/**
 * List all keys for a user.
 *
 * @param {string} authURL - Auth server URL
 * @param {string} userId - User ID (defaults to current user)
 * @param {string} authToken - Auth token
 * @returns {Promise<Object>} List of keys
 */
export async function listUserKeys(authURL, userId, authToken) {
    authURL = authURL || _authURL

    if (!userId) {
        let ready = await checkAuth()
        userId = ready.userId
    }

    let resp = await fetch(`${authURL}/users/${userId}/keys`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
    })

    return resp.json()
}

/**
 * Revoke a specific key.
 *
 * @param {string} authURL - Auth server URL
 * @param {string} userId - User ID
 * @param {string} kid - Key identifier to revoke
 * @param {string} authToken - Auth token (must be from different key)
 * @returns {Promise<Object>} Response
 */
export async function revokeUserKey(authURL, userId, kid, authToken) {
    authURL = authURL || _authURL

    if (!userId) {
        let ready = await checkAuth()
        userId = ready.userId
    }

    let resp = await fetch(`${authURL}/users/${userId}/keys/${kid}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
    })

    return resp.json()
}

/**
 * Get user's public key by userId and kid.
 *
 * @param {string} authURL - Auth server URL
 * @param {string} userId - User ID
 * @param {string} kid - Key identifier
 * @returns {Promise<Object>} Public key info
 */
export async function getUserPublicKey(authURL, userId, kid) {
    authURL = authURL || _authURL

    let resp = await fetch(`${authURL}/users/${userId}/keys/${kid}/public`)
    return resp.json()
}

/**
 * Accept an invite and register as a new user.
 *
 * @param {string} authURL - Auth server URL
 * @param {string} inviteId - Invite ID
 * @param {Object} options - Optional displayName, email
 * @returns {Promise<Object>} Registration response
 */
export async function acceptInvite(authURL, inviteId, options = {}) {
    authURL = authURL || _authURL

    let { userId, deviceId, keys } = await checkAuth()

    let publicKey = await acequia.tokens.jose.exportJWK(keys.publicKey)
    let kid = await acequia.tokens.jose.calculateJwkThumbprint(publicKey)

    let acceptBody = {
        inviteId,
        userId,
        deviceId,
        publicKey,
        kid,
        displayName: options.displayName,
        email: options.email,
    }

    let resp = await fetch(authURL + '/invites/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(acceptBody),
    })

    return resp.json()
}

/**
 * Get invite info (to display to user before accepting).
 *
 * @param {string} authURL - Auth server URL
 * @param {string} inviteId - Invite ID
 * @returns {Promise<Object>} Invite info
 */
export async function getInviteInfo(authURL, inviteId) {
    authURL = authURL || _authURL

    let resp = await fetch(`${authURL}/invites/${inviteId}`)
    return resp.json()
}
